/* eslint-disable max-lines -- legacy */
import { useReactiveVar } from "@apollo/client";
import {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { isKeyboardEvent } from "@dnd-kit/utilities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";

import MainView, { type KanbanScrollViewRef } from "./MainView";
import BoardFilterScope from "@/features/player-kanban/components/organisms/BoardFilterScope";
import BoardShareSheet from "@/features/player-kanban/components/organisms/BoardShareSheet";
import { KanbanColumnSkeleton } from "@/features/player-kanban/components/organisms/KanbanColumn";
import PlayerPreviewDrawer from "@/features/player-kanban/components/organisms/PlayerPreviewDrawer";
import type { BoardInfo } from "@/features/player-kanban/components/organisms/BoardTabs/BoardTabsContainer";

import { MESSAGES } from "../../constants";
import { DEFAULT_FILTER_STATE } from "../../constants/filterDefaults";
import { useBoardScopedSearchSort } from "../../hooks/useBoardScopedSearchSort";
import {
  applyBoardSearchAndSort,
  toFullBoardIndex,
} from "../../utils/boardSearchSort";
import { useBoardDeepLink } from "../../hooks/useBoardDeepLink";
import { usePlayerDeepLink } from "../../hooks/usePlayerDeepLink";
import useBoardPlayerMetrics from "../../hooks/useBoardPlayerMetrics";
import { useBoardManager } from "../../hooks/useBoardManager";
import {
  useBoardSelection,
  type BoardSelectionOutcome,
} from "../../hooks/useBoardSelection";
import { useBoardSelectionPersistence } from "../../hooks/useBoardSelectionPersistence";
import { canShareBoard } from "../../utils/canShareBoard";
import { useBoardPermissions } from "../../hooks/useBoardPermissions";
import { useIsPipelineManager } from "../../hooks/useIsPipelineManager";
import { useBoardFilterOptions } from "../../hooks/useBoardFilterOptions";
import { useKanbanBoard } from "../../hooks/useKanbanBoard";
import { useKanbanLoadingTimeout } from "../../hooks/useKanbanLoadingTimeout";
import { usePipelineCreationFlow } from "../../hooks/usePipelineCreationFlow";
import { usePipelineViewMode } from "../../hooks/usePipelineViewMode";
import { usePipelineRouteTelemetry } from "../../hooks/usePipelineRouteTelemetry";
import { usePlayerSidebar } from "../../hooks/usePlayerSidebar";
import { usePlayerProfileNavigation } from "../../hooks/usePlayerProfileNavigation";
import { useStageDeleteConfirmation } from "../../hooks/useStageDeleteConfirmation";
import { useWorkingStages } from "../../hooks/useWorkingStages";
import DeleteStageDialog from "../../components/molecules/DeleteStageDialog";
import {
  usePlayerPreviewDrawerData,
  type PlayerPreviewDrawerData,
} from "../../hooks/usePlayerPreviewDrawerData";
import type {
  ActiveDragState,
  ColumnData,
  CreatePlayerDropHandler,
  DragOverlayPlayerData,
} from "../../types/types";
import { useBoardIdentity } from "../../hooks/useBoardIdentity";
import { BOARD_LIST_UNAVAILABLE_FALLBACK } from "../../utils/boardIdentity";
import { resolveColumnInsertionIndex } from "../../utils/columnInsertion";
import { useKanbanCardsUiTreatment } from "../../hooks/useKanbanCardsUiTreatment";
import { computePhasePositions } from "../../utils/phasePositions";
import { resolveServerGpvRange } from "../../utils/playerFilter";

import { Alert } from "@/components/atoms/Alert";
import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { MY_ROSTER_KANBAN } from "@/config/featureFlags";
import { isPipelineSurfaceOn } from "@/config/pipelineSurface";
import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import { KanbanSourceType, UserKanbanBoardRole } from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useToastNotification } from "@/hooks/useToastNotification";
import TableFilterDrawer from "../../components/organisms/TableFilterDrawer";
import { myRosterSelectedVar } from "@/stores/watchlistSelection";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 * Calculate the insertion index for a player drop.
 *
 * SE-6554 item N: this used to derive the slot from the rect of the COLUMN being
 * dropped on, which made it a constant per column — the card landed in a fixed
 * slot regardless of where the user aimed. It now delegates to the SAME function
 * the lane uses to position its live drop gap, measured against the DRAGGED
 * card's rect, so the card lands exactly in the gap that opened for it.
 * @param draggedRect - The dragged card's current rect (`active.rect.current.translated`)
 * @param column - Column data containing players, as rendered
 * @returns Calculated insertion index into the rendered column
 */
function calculateInsertionIndex(
  draggedRect: { top: number; height: number } | null | undefined,
  column: ColumnData | undefined
): number {
  return resolveColumnInsertionIndex(column, draggedRect);
}

/**
 * The column a coordinate-derived drop index must be measured against — SE-7405
 * widened "as rendered" to search+sort+kanban-filter narrowed (previously just
 * search+sort), so this reads the kanban-filter-narrowed set and falls back to
 * the full-board column when the rendered set doesn't have it yet (e.g. still
 * loading). `renderedColumns` is the SAME reference as the search+sort-only set
 * whenever no kanban filter is active (see `applyTableFilterPassSet`'s null-pass
 * contract), so this is a no-op change whenever SE-7405's panel isn't in use.
 * @param renderedColumns - Columns as actually painted
 * @param columnId - The column to find
 * @param fallback - The full-board column, used if the rendered set lacks it
 * @returns The column to measure a coordinate-derived index against
 */
function resolveRenderedColumn(
  renderedColumns: ColumnData[] | undefined,
  columnId: string | undefined,
  fallback: ColumnData | undefined
): ColumnData | undefined {
  return renderedColumns?.find(col => col.id === columnId) ?? fallback;
}

/**
 * As much of a dnd-kit `active` as a measurement needs. Every level is optional
 * because a synthetic drag event is free to omit them — see `draggedRectOf`.
 */
interface DragActiveMeasurement {
  rect?: { current?: { translated?: { top: number; height: number } | null } };
}

/**
 * The dragged card's current rect, as dnd-kit reports it during the drag.
 *
 * Read defensively: a real sensor-driven drag always carries
 * `active.rect.current`, but a synthetic drag event does not have to, and a
 * drop must never throw on its way to persisting a move. A missing rect degrades
 * to "no measurement", which `resolveColumnInsertionIndex` turns into an append
 * — the same thing the user sees when they drop past the last card.
 * @param dragActive - The `active` member of a dnd-kit drag event
 * @returns The dragged rect, or null when the event carries no measurement
 */
const draggedRectOf = (
  dragActive: DragActiveMeasurement
): { top: number; height: number } | null =>
  dragActive.rect?.current?.translated ?? null;

/**
 * Check if drag operation is a removal drop
 * @param dropTarget - Drop target data
 * @param draggedData - Dragged item data
 * @returns True if this is a valid removal drop
 */
function isValidRemovalDrop(
  dropTarget: DragOverlayPlayerData | undefined,
  draggedData: DragOverlayPlayerData | undefined
): boolean {
  return Boolean(
    dropTarget &&
    "type" in dropTarget &&
    dropTarget.type === "removal" &&
    draggedData?.source === "kanban" &&
    draggedData
  );
}

/** Stable options for the dnd-kit KeyboardSensor (SE-4500). */
const keyboardSensorOptions = {
  coordinateGetter: sortableKeyboardCoordinates,
};

/** Stable options for the dnd-kit PointerSensor. */
const pointerSensorOptions = { activationConstraint: { distance: 3 } };

/** Stable options for the dnd-kit TouchSensor. */
const touchSensorOptions = {
  activationConstraint: { distance: 3, delay: 16, tolerance: 5 },
};

/**
 * Resolve a within-column rank drag-reorder drop (SE-4685). Returns the phase +
 * active/over player ids only for a valid same-column rank-card drop; null for
 * a cross-column drag or a non-reorder target (cross-column drops are resolved
 * separately by resolveRankCrossColumnDrop — SE-5483).
 * @param draggedData - Drag data of the dragged rank card
 * @param dropTarget - Drag data of the drop target
 * @returns The resolved reorder, or null when the drop is not a same-column rank drop
 */
const resolveRankReorderDrop = (
  draggedData: DragOverlayPlayerData | undefined,
  dropTarget: DragOverlayPlayerData | undefined
): {
  columnId: string;
  activePlayerId: string;
  overPlayerId: string;
} | null => {
  const columnId = draggedData?.columnId;
  const activePlayerId = draggedData?.playerId;
  const overPlayerId = dropTarget?.playerId;
  if (!columnId || !activePlayerId || !overPlayerId) return null;
  if (activePlayerId === overPlayerId) return null;
  if (dropTarget?.dragType !== "rank-reorder") return null;
  if (dropTarget.columnId !== columnId) return null;
  return { columnId, activePlayerId, overPlayerId };
};

/**
 * SE-6556: DOM selector for the rank-reorder grab handle rendered inside every
 * rank-sorted card (KanbanPlayerCardView). The handle's `testID` is its stable
 * public identity — the same one the e2e suite and `kanban.helpers` already
 * address it by — so matching on it keeps this check independent of the card's
 * internal markup.
 */
const RANK_HANDLE_SELECTOR = `[data-testid="${TEST_ID_PLAYER_KANBAN.PLAYER_CARD_RANK_DRAG_HANDLE}"]`;

/**
 * Whether a rank-reorder drag was activated from the RANK HANDLE rather than
 * from the card body (SE-6556).
 *
 * Both gestures activate the SAME sortable node: RankSortableCard spreads
 * dnd-kit's activator listeners on the whole row (SE-4685) so an in-stage
 * reorder keeps working by either gesture — SE-5493 shipped that and it passed
 * QA, and it is deliberately unchanged here. The gesture is therefore only
 * recoverable from the activation event's DOM target, which dnd-kit hands back
 * on every drag event as `activatorEvent` (the native event the sensor
 * activated from).
 *
 * Keyboard activation counts as handle activation: the sortable node itself is
 * the focusable activator, so a keyboard drag carries no body-vs-handle
 * distinction and must keep the full cross-stage capability (SE-4500). The
 * keyboard test uses dnd-kit's own `isKeyboardEvent` so it cannot drift from
 * the predicate the KeyboardSensor is selected by. Anything that is neither — a
 * missing or non-DOM activator — fails CLOSED, because the default this guard
 * exists to remove is "cross-stage happens by accident".
 * @param activatorEvent - The native event dnd-kit activated the drag from
 * @returns True when this drag is allowed to move the card across stages
 */
const isRankHandleActivation = (
  activatorEvent: Event | null | undefined
): boolean => {
  if (!activatorEvent) return false;
  if (isKeyboardEvent(activatorEvent)) return true;
  const target = activatorEvent.target as Element | null;
  if (typeof target?.closest !== "function") return false;
  return target.closest(RANK_HANDLE_SELECTOR) !== null;
};

/**
 * Resolve a cross-column rank-handle drop (SE-5483). A rank-reorder drag that
 * ends over ANOTHER column routes to the same card-move path as a body drag,
 * with the drop position setting the card's rank in the target column: over a
 * rank card → that card's index; over the column droppable → the coordinate-
 * derived insertion index (empty column → 0).
 * @param draggedData - Drag data of the dragged rank card
 * @param dropTarget - Drag data of the drop target
 * @param columnsData - Current board columns, used to locate the target column
 * @param draggedRect - The dragged card's current rect, for column-droppable drops
 * @param visibleColumnsData - Columns as RENDERED (search/sort applied), which
 * a coordinate-derived index must be measured against before translation
 * @returns The target column + player + insertion index, or null when the drop
 * is not a cross-column rank drop
 */
const resolveRankCrossColumnDrop = (
  draggedData: DragOverlayPlayerData | undefined,
  dropTarget: DragOverlayPlayerData | undefined,
  columnsData: ColumnData[] | undefined,
  draggedRect: { top: number; height: number } | null | undefined,
  visibleColumnsData?: ColumnData[] | undefined
): { columnId: string; playerId: string; insertionIndex: number } | null => {
  const sourceColumnId = draggedData?.columnId;
  const playerId = draggedData?.playerId;
  const targetColumnId = dropTarget?.columnId;
  if (!sourceColumnId || !playerId || !targetColumnId) return null;
  if (targetColumnId === sourceColumnId) return null;

  const targetColumn = columnsData?.find(col => col.id === targetColumnId);

  if (dropTarget.dragType === "rank-reorder") {
    if (!targetColumn) return null;
    // Resolved by card IDENTITY, so it is already a full-board index and needs
    // no translation — dropping onto a visible card means "take that card's
    // position", wherever that card sits in the unfiltered column.
    const overIndex = targetColumn.players.findIndex(
      p => p.id === dropTarget.playerId
    );
    return {
      columnId: targetColumnId,
      playerId,
      insertionIndex: overIndex >= 0 ? overIndex : targetColumn.players.length,
    };
  }

  if (dropTarget.type === "column") {
    // SE-6488: a coordinate-derived index is measured against the RENDERED
    // column, so it must be computed on the visible rows and then translated
    // back to the full column before it is persisted. Measuring it against the
    // unfiltered column while a search is active drops the card at a different
    // position from the one the user saw.
    const visibleColumn =
      visibleColumnsData?.find(col => col.id === targetColumnId) ??
      targetColumn;
    return {
      columnId: targetColumnId,
      playerId,
      insertionIndex: toFullBoardIndex(
        targetColumn?.players ?? [],
        visibleColumn?.players ?? [],
        calculateInsertionIndex(draggedRect, visibleColumn)
      ),
    };
  }

  return null;
};

/** Everything {@link dispatchRankDrop} needs to resolve one rank drop. */
interface RankDropArgs {
  /** Drag data of the dragged rank card. */
  readonly draggedData: DragOverlayPlayerData;
  /** Drag data of the drop target. */
  readonly dropTarget: DragOverlayPlayerData | undefined;
  /** Current board columns. */
  readonly columnsData: ColumnData[] | undefined;
  /** The dragged card's current rect. */
  readonly draggedRect: { top: number; height: number } | null | undefined;
  /** Native event dnd-kit activated the drag from. */
  readonly activatorEvent: Event | null | undefined;
  /** In-column reorder dispatcher. */
  readonly updateCardPositions: (
    columnId: string,
    activePlayerId: string,
    overPlayerId: string
  ) => void;
  /** Cross-column move dispatcher factory. */
  readonly createPlayerDropHandler: CreatePlayerDropHandler;
  /** Columns as RENDERED (search/sort applied). */
  readonly visibleColumnsData?: ColumnData[];
  /** Whether the rendered order still IS rank order. Defaults to true. */
  readonly isRankOrderRendered?: boolean;
}

/**
 * Dispatch a rank drop: same-column → optimistic in-column reorder (SE-4685);
 * cross-column → the shared card-move path with the drop index setting the
 * card's rank in the target column (SE-5483), but ONLY when the drag was
 * activated from the rank handle (SE-6556). Any other target no-ops (the card
 * snaps back).
 *
 * SE-6556: the cross-stage branch is gated on the gesture because SE-5493's
 * resolved decision (2026-07-16) puts cross-stage movement on the rank handle
 * alone. A card-body drag still activates the same sortable — that is what
 * makes in-stage reorder work by either gesture — so without this gate it also
 * commits a cross-stage move. Gating here (rather than at activation) is what
 * leaves in-stage reorder untouched: a body drag that lands inside its own
 * stage still reorders, and only a body drag that lands on ANOTHER stage
 * no-ops.
 * SE-7470 bundled the nine positional parameters into one object. The
 * behaviour is untouched — same values, same order, same
 * `isRankOrderRendered` default. `columnsData` and `visibleColumnsData` are
 * both `ColumnData[] | undefined` and sat four positions apart, so a
 * transposition at the call site would have type-checked and silently resolved
 * cross-column drops against the unfiltered board; named properties make that
 * unexpressible.
 * @param args - {@link RankDropArgs}
 * @param args.draggedData - Drag data of the dragged rank card
 * @param args.dropTarget - Drag data of the drop target
 * @param args.columnsData - Current board columns
 * @param args.draggedRect - The dragged card's current rect
 * @param args.activatorEvent - Native event dnd-kit activated the drag from
 * @param args.updateCardPositions - In-column reorder dispatcher
 * @param args.createPlayerDropHandler - Cross-column move dispatcher factory
 * @param args.visibleColumnsData - Columns as RENDERED (search/sort applied)
 * @param args.isRankOrderRendered - Whether the rendered order still IS rank order
 */
const dispatchRankDrop = ({
  draggedData,
  dropTarget,
  columnsData,
  draggedRect,
  activatorEvent,
  updateCardPositions,
  createPlayerDropHandler,
  visibleColumnsData,
  isRankOrderRendered = true,
}: RankDropArgs): void => {
  const rankDrop = resolveRankReorderDrop(draggedData, dropTarget);
  if (rankDrop) {
    // SE-6488: an in-column rank reorder means "put A where B is IN RANK
    // ORDER". While a board-level sort is active the rendered order is not the
    // rank order, so the position the user sees and the position that would be
    // written are different things. Refuse the drop (the card snaps back)
    // rather than persisting an order they did not choose. This mirrors the
    // per-column rule, where the rank handle is live only under `sortBy=rank`.
    // A board SEARCH is fine: filtering preserves rank order as a subsequence.
    if (!isRankOrderRendered) return;
    updateCardPositions(
      rankDrop.columnId,
      rankDrop.activePlayerId,
      rankDrop.overPlayerId
    );
    return;
  }
  if (!isRankHandleActivation(activatorEvent)) return;
  const crossColumnDrop = resolveRankCrossColumnDrop(
    draggedData,
    dropTarget,
    columnsData,
    draggedRect,
    visibleColumnsData
  );
  if (crossColumnDrop) {
    createPlayerDropHandler(crossColumnDrop.columnId)({
      ...draggedData,
      id: crossColumnDrop.playerId,
      insertionIndex: crossColumnDrop.insertionIndex,
    });
  }
};

/** User edge shape from the ListMyKanbanBoards query response */
type BoardUserEdge = { node: { userId: string; role: UserKanbanBoardRole } };

/** Active board shape relevant to role derivation */
type ActiveBoardForRole = {
  users?: { edges?: readonly unknown[] } | null;
  createdBy?: string | null;
} | null;

/** Whether the pipeline surface (SE-6071) wins the source-type resolution. */
type PipelineAccess = "pipeline" | "fallback";
/** Which non-pipeline source the user has selected. */
type FallbackSource = "roster" | "watchlist";

const KANBAN_SOURCE_TYPES: Record<
  PipelineAccess,
  Record<FallbackSource, KanbanSourceType>
> = {
  pipeline: {
    roster: KanbanSourceType.Pipeline,
    watchlist: KanbanSourceType.Pipeline,
  },
  fallback: {
    roster: KanbanSourceType.Roster,
    watchlist: KanbanSourceType.Watchlist,
  },
};

/**
 * Resolves the active player source for the kanban surface.
 *
 * Precedence (SE-6071): when the PIPELINE_SURFACE flag is on AND the user is
 * Manager+, the surface flips to the migrated PIPELINE-type boards (SE-5505),
 * winning over the roster/watchlist logic. Otherwise it falls through to the
 * existing behavior — roster when both the MY_ROSTER_KANBAN flag and the user
 * toggle are on, else watchlist. A sub-Manager (or a flag-off build) never
 * resolves PIPELINE, so production/watchlist behavior is unchanged until the
 * flag is deliberately enabled.
 * @param isMyRosterEnabled - Whether the MY_ROSTER_KANBAN flag is on
 * @param isMyRosterSelected - Whether the user toggled the roster source
 * @param isPipelineEnabled - Whether the PIPELINE_SURFACE flag is on
 * @param isManagerPlus - Whether the current user is Manager+ (Owner/Admin/Manager)
 * @returns Kanban source type for the active board
 */
/**
 * How long the unresolved-deep-link toast stays up, in ms.
 *
 * The shared default is 1500ms, tuned for short confirmations ("Added to
 * pipeline"). This message has to explain a failure the user did not expect and
 * name two possible causes, so it needs long enough to actually be read —
 * otherwise it is only marginally less silent than showing nothing.
 */
const UNRESOLVED_DEEP_LINK_TOAST_DURATION_MS = 6000;

/**
 * Shows the "board deep-link unresolved" toast (SE-7120).
 * @remarks
 * A dedicated hook (not inline in MainContainer) for the same reason as
 * `renderLoadingTimeoutState` above — the container's already oversized body
 * has to stay inside the `max-lines-per-function` ratchet.
 * @param toast - The toast notification API.
 * @returns A stable callback that shows the unresolved-deep-link toast.
 */
const useUnresolvedBoardDeepLinkToastHandler = (
  toast: ReturnType<typeof useToastNotification>
) =>
  useCallback(() => {
    // Never silent. Landing on a plausible-looking wrong board with no
    // feedback is the defect; the fallback board itself is fine.
    toast.show({
      action: "danger",
      description: MESSAGES.ERROR.BOARD_DEEP_LINK_UNRESOLVED,
      duration: UNRESOLVED_DEEP_LINK_TOAST_DURATION_MS,
    });
  }, [toast]);

/**
 * Reports the outcome of a pipeline pick made in the chip row or the "See all
 * pipelines" drawer (SE-6270).
 * @remarks
 * The switch is EXHAUSTIVE over {@link BoardSelectionOutcome} on purpose: the
 * four states this feature distinguishes — switched, still-arriving, list
 * known-incomplete, genuinely absent — must each keep their own answer, and
 * adding a fifth must fail to compile here rather than fall through to silence.
 * That silence is the defect: `handleBoardSelect` used to call `switchBoard` and
 * discard its boolean, so a pick the board manager could not resolve produced no
 * board change and no message at all.
 *
 * `pending` never reaches this handler — {@link useBoardSelection} holds a
 * provisional miss and retries it — but it is listed so the union stays closed.
 * Inventing an error for a board that is merely still loading would be its own
 * (quieter) lie.
 *
 * A dedicated hook rather than an inline callback, for the same reason as the
 * deep-link handler above: the container body has to stay inside the
 * `max-lines-per-function` ratchet.
 * @param toast - The toast notification API.
 * @returns A stable callback that reports a settled selection outcome.
 */
const useBoardSelectionOutcomeToastHandler = (
  toast: ReturnType<typeof useToastNotification>
) =>
  useCallback(
    (outcome: BoardSelectionOutcome): void => {
      switch (outcome.kind) {
        case "switched":
          // The board changed under the user; the board itself is the feedback.
          return;
        case "pending":
          // Held and retried by the hook. Nothing has failed, so say nothing.
          return;
        case "incomplete":
          toast.show({
            action: "danger",
            description: MESSAGES.ERROR.BOARD_SELECTION_INCOMPLETE,
            duration: UNRESOLVED_DEEP_LINK_TOAST_DURATION_MS,
          });
          return;
        case "absent":
          toast.show({
            action: "danger",
            description: MESSAGES.ERROR.BOARD_SELECTION_UNRESOLVED,
            duration: UNRESOLVED_DEEP_LINK_TOAST_DURATION_MS,
          });
          return;
        default: {
          const unhandled: never = outcome;
          return unhandled;
        }
      }
    },
    [toast]
  );

/** Arguments for {@link useSwitcherBoardSelection}. */
interface SwitcherBoardSelectionArgs {
  /** The toast notification API. */
  readonly toast: ReturnType<typeof useToastNotification>;
  /** `useBoardManager`'s `switchBoard`; `false` means the id did not match. */
  readonly switchBoard: (boardId: string) => boolean;
  /** True while the resolved board set can still grow. */
  readonly boardSetPending: boolean;
  /** True when the board set is known NOT to be the whole set. */
  readonly hasUnreachableBoards: boolean;
}

/**
 * The switcher's board-pick handler: {@link useBoardSelection} wired to the
 * toast reporter above (SE-6270).
 * @remarks
 * Composed here rather than in the container body for the same
 * `max-lines-per-function` reason as the two hooks above — and because the two
 * halves are meaningless apart: the hook exists to produce an outcome, and the
 * reporter exists to answer for it.
 * @param args - The {@link SwitcherBoardSelectionArgs}.
 * @param args.toast - The toast notification API.
 * @param args.switchBoard - Activates a board by id; `false` means no match.
 * @param args.boardSetPending - True while the board set can still grow.
 * @param args.hasUnreachableBoards - True when the board set is known partial.
 * @returns A stable `(boardId: string) => void` handler for chip and drawer picks.
 */
const useSwitcherBoardSelection = ({
  toast,
  switchBoard,
  boardSetPending,
  hasUnreachableBoards,
}: SwitcherBoardSelectionArgs): ((boardId: string) => void) => {
  const onOutcome = useBoardSelectionOutcomeToastHandler(toast);
  return useBoardSelection({
    switchBoard,
    boardSetPending,
    hasUnreachableBoards,
    onOutcome,
  });
};

const resolveKanbanSourceType = (
  isMyRosterEnabled: boolean,
  isMyRosterSelected: boolean,
  isPipelineEnabled: boolean,
  isManagerPlus: boolean
): KanbanSourceType => {
  const pipelineAccess: PipelineAccess =
    isPipelineEnabled && isManagerPlus ? "pipeline" : "fallback";
  const fallbackSource: FallbackSource =
    isMyRosterEnabled && isMyRosterSelected ? "roster" : "watchlist";

  return KANBAN_SOURCE_TYPES[pipelineAccess][fallbackSource];
};

/**
 * Derives the current user's role on the active board, using board list edges
 * with a fallback to creator-implied Owner role (SE-3569 workaround)
 * @param activeBoard - Currently active board, or null
 * @param userId - Current user ID
 * @returns The derived role, or null when no role can be inferred
 */
const deriveActiveBoardUserRole = (
  activeBoard: ActiveBoardForRole,
  userId: string | undefined
): UserKanbanBoardRole | null => {
  if (!activeBoard) return null;
  const edges = (activeBoard.users?.edges ?? []) as unknown as BoardUserEdge[];
  const userBoardEntry = edges.find(edge => edge.node.userId === userId);
  if (userBoardEntry) return userBoardEntry.node.role;
  return activeBoard.createdBy === userId ? UserKanbanBoardRole.Owner : null;
};

/**
 * Full-screen state shown when the board's initial load exceeds the timeout.
 * @remarks
 * Module-scope (not inline in MainContainer) so the container's already
 * oversized body stays inside the `max-lines-per-function` ratchet — it takes
 * no container state beyond the retry callback.
 * @param onRetry - Re-issues the board queries
 * @returns The timeout state element
 */
const renderLoadingTimeoutState = (onRetry: () => void) => (
  <Box className="flex-1 items-center justify-center p-6">
    <Stack className="max-w-96 items-center gap-4">
      <Alert>
        <Stack className="gap-2">
          <Text variant="body-strong" className="text-status-error">
            Loading Timeout
          </Text>
          <Text variant="body" className="text-content-secondary">
            The Kanban board is taking longer than expected to load. This might
            be due to network issues or temporary server problems.
          </Text>
        </Stack>
      </Alert>
      <Pressable
        onPress={onRetry}
        className="rounded-md bg-accent-primary px-6 py-3"
      >
        <Text variant="body-strong" className="text-content-on-accent">
          Retry Loading
        </Text>
      </Pressable>
      <Text variant="body" className="text-content-muted">
        If the problem persists, please try refreshing the page.
      </Text>
    </Stack>
  </Box>
);

/**
 * Full-screen state shown when the board LIST could not be read at all
 * (SE-6491, the `unavailable` board identity).
 *
 * Before this existed, a failed or refused `listKanbanBoards` left `boards`
 * empty and the surface rendered its no-boards affordance — telling a user who
 * may well own several pipelines that they have none. "We could not ask" and
 * "there is nothing" are different answers and this is the one that says so.
 *
 * Module-scope for the same reason as `renderLoadingTimeoutState` above.
 * @param reason - The underlying board-list error message.
 * @param onRetry - Re-issues the board-list query.
 * @returns The unavailable state element
 */
const renderBoardsUnavailableState = (reason: string, onRetry: () => void) => (
  <Box className="flex-1 items-center justify-center p-6">
    <Stack className="max-w-96 items-center gap-4">
      <Alert>
        <Stack className="gap-2">
          <Text variant="body-strong" className="text-status-error">
            Boards unavailable
          </Text>
          <Text variant="body" className="text-content-secondary">
            {BOARD_LIST_UNAVAILABLE_FALLBACK}
          </Text>
          <Text variant="caption" className="text-content-muted">
            {reason}
          </Text>
        </Stack>
      </Alert>
      <Pressable
        onPress={onRetry}
        className="rounded-md bg-accent-primary px-6 py-3"
        testID="kanban-boards-unavailable-retry"
      >
        <Text variant="body-strong" className="text-content-on-accent">
          Try again
        </Text>
      </Pressable>
    </Stack>
  </Box>
);

/**
 * Fallback message for the merged failed-board / failed-cards error screen
 * (SE-7624) — used only when there is no more specific `error` string. "That
 * is not the same as the board being empty" is load-bearing: it is the whole
 * reason `failed` and `empty` are separate cards-read states rather than one
 * "nothing to show" screen. Reuses the SAME error screen `error` already
 * renders (see the merged `if (error || cardsUiTreatment.kind === "failed")`
 * check below) rather than a second full-screen state, so a failed cards
 * read and a failed board read look identical to the user — both are "we
 * could not read this, try again."
 */
const CARDS_ERROR =
  "We couldn't load this board's cards. That is not the same as the board being empty — try again.";

/** Disclosure copy for the cards-`stale` state (SE-7624). */
const CARDS_STALE_MESSAGE =
  "These cards may be out of date — the last refresh failed.";

/**
 * Staleness banner rendered ABOVE the board when the cards read errored but
 * cached cards are still on hand (SE-7624, the `stale` cards read state).
 *
 * `errorPolicy: "all"` (see `providers/ApolloClient.tsx`) hands back cached
 * data ALONGSIDE the error instead of rejecting, so the right answer is to
 * keep showing the board — not to discard rows the user already had — while
 * disclosing that the last refresh failed.
 * @param onRetry - Re-issues the board's queries, including the cards read.
 * @returns The staleness banner element
 */
const renderCardsStaleBanner = (onRetry: () => void) => (
  <Box accessibilityRole="alert">
    <Stack
      direction="horizontal"
      className="items-center justify-between gap-2 border-b border-outline-subtle bg-status-warning-surface px-4 py-2"
    >
      <Text variant="caption" className="shrink text-status-warning-strong">
        {CARDS_STALE_MESSAGE}
      </Text>
      <Pressable onPress={onRetry} testID="kanban-cards-stale-retry">
        <Text variant="caption" className="text-accent-primary">
          Retry
        </Text>
      </Pressable>
    </Stack>
  </Box>
);

/**
 * How many card placeholders each skeleton lane shows (Figma 80:16484).
 *
 * Deliberately uneven — the node draws 3/2/3/2/1/2. A board mid-load has
 * different amounts in each stage, and repeating the same count down every lane
 * would read as a loading GRAPHIC rather than as the board arriving.
 */
const SKELETON_LANE_CARDS: readonly number[] = [3, 2, 3, 2, 1, 2];

/**
 * The board's loading state (Figma 80:16484) — a skeleton of the board, not a
 * spinner.
 *
 * It replaces a centred spinner plus "Loading player pipeline…". The skeleton
 * is the better answer for this surface because the board's shape is the same
 * every time: showing the lanes immediately tells the user what is coming and
 * where, and because the lanes match the real ones in width and radius, nothing
 * moves when the data lands.
 *
 * The whole thing is hidden from assistive tech — it is decorative, and a
 * screen reader announcing a dozen empty placeholder boxes would be worse than
 * silence. The timeout notice below it is real text and stays announced.
 *
 * Module-scope for the same reason as `renderLoadingTimeoutState` above — the
 * container's already oversized body has to stay inside the
 * `max-lines-per-function` ratchet.
 * @param hasTimedOut - Whether the load is running long (but has not yet timed
 * out). The notice is the ONLY thing that still speaks to the user here, so it
 * survives the spinner's removal.
 * @returns The skeleton board.
 */
const renderBoardSkeleton = (hasTimedOut: boolean) => (
  <Box className="flex-1 bg-surface-raised">
    {/* Toolbar ghost — four bars where the board title and its controls go. */}
    <Stack
      aria-hidden
      direction="horizontal"
      className="items-center gap-2 bg-surface-base px-5 py-3"
    >
      <Skeleton variant="rounded" className="h-3 w-44" />
      <Skeleton variant="rounded" className="h-3 w-24" />
      <Skeleton variant="rounded" className="h-3 w-14" />
      <Skeleton variant="rounded" className="h-3 w-14" />
    </Stack>

    <Stack direction="horizontal" className="items-start gap-4 px-5 py-4">
      {SKELETON_LANE_CARDS.map((cardCount, index) => (
        <KanbanColumnSkeleton key={index} cardCount={cardCount} />
      ))}
    </Stack>

    {hasTimedOut && (
      <Text variant="body" className="px-5 text-status-warning">
        This is taking longer than usual...
      </Text>
    )}
  </Box>
);

/**
 * SE-5494: the player preview drawer, opened by clicking a board card.
 *
 * Rendered at the screen root (not inside the board row) because it is an
 * overlay, and so it sits in the same layer as the stage-move prompt it
 * cooperates with: selecting a protected/terminal stage in the drawer routes
 * through `movePlayerToPhase`, which raises that prompt. One move service, one
 * prompt, two entry points (AC4).
 *
 * Module-scope for the same reason as `renderLoadingTimeoutState` above — the
 * container's already oversized body has to stay inside the
 * `max-lines-per-function` ratchet.
 * @param props - Everything the drawer needs from the screen.
 * @param props.selectedPlayerId - The player whose preview is open
 * @param props.onClose - Clears the selection
 * @param props.drawerData - Board-derived stages/tags/stage/days-in-stage
 * @param props.boardId - The active board, scoping the drawer's audit trail
 * @param props.canEdit - Whether the viewer may move this card
 * @param props.isPipelineBoard - Whether the active board is a PIPELINE board
 * @param props.onMoveToStage - The board's SHARED stage-move service
 * @param props.onOpenProfile - Navigates to the full player profile
 * @param props.onViewAllNotes - Navigates to the profile's Reports & Notes tab
 * @param props.onRemove - Removes the card, recording the confirmed reason
 * @returns The drawer element.
 */
const renderPlayerPreviewDrawer = ({
  selectedPlayerId,
  onClose,
  drawerData,
  boardId,
  canEdit,
  isPipelineBoard,
  onMoveToStage,
  onOpenProfile,
  onViewAllNotes,
  onRemove,
}: {
  selectedPlayerId?: string | null;
  onClose: () => void;
  drawerData: PlayerPreviewDrawerData;
  boardId?: string;
  canEdit: boolean;
  isPipelineBoard: boolean;
  onMoveToStage: (
    playerId: string,
    fromPhaseId: string,
    toPhaseId: string
  ) => Promise<void>;
  onOpenProfile: (playerId: string) => void;
  onViewAllNotes: (playerId: string) => void;
  onRemove: (playerId: string, rationale: string) => Promise<void>;
}) => (
  <PlayerPreviewDrawer
    selectedPlayerId={selectedPlayerId}
    onClose={onClose}
    stages={drawerData.stages}
    currentStageId={drawerData.currentStageId}
    tags={drawerData.tags}
    daysInStage={drawerData.daysInStage}
    // SE-5504: the two ids that scope the drawer's audit trail server-side.
    kanbanBoardId={boardId}
    kanbanCardId={drawerData.kanbanCardId}
    canEdit={canEdit}
    isPipelineBoard={isPipelineBoard}
    onMoveToStage={onMoveToStage}
    onOpenProfile={onOpenProfile}
    // "View all notes" lands on the profile, whose Reports & Notes tab owns the
    // full list — the drawer deliberately does not reimplement it. It is its
    // OWN handler rather than an alias of `onOpenProfile`: aliasing dropped the
    // user on Overview, one more click from the list they asked for.
    onViewAllNotes={onViewAllNotes}
    onRemove={onRemove}
  />
);

/**
 * MainContainer - Container component for the Kanban board main screen
 * @remarks
 * Uses Pressable components instead of HTML buttons for cross-platform compatibility.
 * Handles board management, drag-and-drop, filters, and player pipeline visualization.
 */
const MainContainer = () => {
  const isSmallScreen = useMobileScreen();
  const currentUser = useCurrentUser();
  const toast = useToastNotification();
  const isMyRosterEnabled = MY_ROSTER_KANBAN === "on";

  // SE-6071: flip the surface to the migrated PIPELINE boards (SE-5505) for
  // Manager+ users when the flag is on. Both guards default the surface OFF —
  // the flag resolves from a build-time static const that defaults "off" and
  // the Manager+ gate fails closed while auth hydrates — so prod/watchlist
  // behavior is unchanged until the flag is deliberately enabled.
  //
  // SE-6257: resolved through `isPipelineSurfaceOn()` — the SAME switch
  // `useIsPipelineSurfaceActive` consults — so a non-production runtime
  // override flips the nav promotion and this source-type resolution in
  // lockstep. Routing only one of the two would resolve a non-PIPELINE source
  // type and `useBoardManager` would return no pipeline boards.
  const isPipelineEnabled = isPipelineSurfaceOn();
  const isManagerPlus = useIsPipelineManager();

  // Get current player source (pipeline, roster, or watchlist)
  const isMyRosterSelected = useReactiveVar(myRosterSelectedVar);
  const sourceType = resolveKanbanSourceType(
    isMyRosterEnabled,
    isMyRosterSelected,
    isPipelineEnabled,
    isManagerPlus
  );

  // SE-5489: the TopPanel center forks on this. A PIPELINE source type is only
  // resolved when the SE-6071 flag is on AND the user is Manager+ (see
  // resolveKanbanSourceType), so this is the single, surface-level signal that
  // the pipeline chip switcher should replace the legacy BoardTabs strip. When
  // the flag is off (or the user is sub-Manager) the surface stays
  // watchlist/roster and BoardTabs renders unchanged — the regression the
  // SE-5489 rework prevents.
  const isPipelineSurface = sourceType === KanbanSourceType.Pipeline;

  // Board management system
  const boardManagerResult = useBoardManager(sourceType);
  const {
    boards,
    hasUnreachableBoards,
    isResolvingBoardSet,
    activeBoard,
    isLoading: boardsLoading,
    // SE-6491: no longer discarded. `useBoardIdentity` reads it off
    // `boardManagerResult` and resolves the `unavailable` identity from it.
    error: _boardsError,
    switchBoard,
    createBoard,
    updateBoardName: updateBoardNameManager,
    deleteBoard,
    // duplicateBoard, // TODO: Not implemented in useBoardManager yet
    clearError: _clearBoardsError,
    refresh: refreshBoards,
  } = boardManagerResult;

  // Use optimized useKanbanBoard hook with proper backend persistence
  const kanbanBoardResult = useKanbanBoard(
    activeBoard?.id,
    activeBoard?.name || "Player Pipeline"
  );
  const {
    board: _board,
    // SE-6561: the RAW card set. `deletePhase`'s occupancy guard filters this
    // exact array, so the delete confirmation must count over it too — the
    // rendered columns exclude closed and player-less cards and would let the
    // dialog promise a delete the guard then refuses.
    cards: boardCards,
    // SE-7624: the cards read's resolution state — distinguishes a genuinely
    // empty board from a failed or stale read, which used to be
    // indistinguishable (both rendered as empty columns).
    cardsState,
    // SE-8534: non-null only when the board identity read was denied for
    // lack of a `user_kanban_board` grant — a permanent condition the error
    // screen below must render WITHOUT a retry affordance.
    accessDeniedMessage,
    columnsData,
    tableColumnsData,
    // SE-5496: Closed area dataset + reopen affordances.
    closedGroups,
    workingPhases,
    reopenCard,
    isLoading,
    error,
    isSaving: _isSaving,
    handlePlayerDrop,
    updateBoardName: _updateBoardName,
    updatePhaseName,
    addPhase,
    deletePhase,
    removePlayerFromBoard,
    updatePhasePositions,
    updateCardPositions,
    clearError,
    // SE-5495: the rendered sensitive-stage reason prompt (hook-returns-JSX,
    // matching useBoardManagement's deleteDialogComponent).
    stageMovePromptComponent,
    // SE-5494: the SHARED stage-move service. The preview drawer's stage
    // selector calls this exact function, so a drawer move and a drag move are
    // one code path with one gate and one prompt instance (AC4).
    movePlayerToPhase,
    // SE-5494: board stages for the drawer's stage selector.
    phases,
    refetch: refetchKanbanBoard,
    // Roster board info
    isRosterBoard,
    playerCount,
  } = kanbanBoardResult;

  // SE-7624: which visual treatment (content/failed/stale) cardsState maps to.
  const cardsUiTreatment = useKanbanCardsUiTreatment(cardsState);

  // SE-8534: a confirmed permission denial — from EITHER the board identity
  // read (accessDeniedMessage) or the cards read (cardsUiTreatment.kind ===
  // "denied"). Consumed by an early return further down — BEFORE the generic
  // failed-read screen — so it renders WITHOUT a retry affordance. Retrying a
  // denial cannot succeed: SE-7918 measured 17 identical denied
  // `ListKanbanCards` requests (plus matching `ListKanbanBoardTags` requests)
  // in 15 minutes from a UI that offered nothing else to do but retry. The
  // fix is to say so once, in the server's own words, and stop.
  const deniedMessage =
    accessDeniedMessage ??
    (cardsUiTreatment.kind === "denied" ? cardsUiTreatment.message : null);

  // Get current board ID for permissions and sharing
  const currentBoardId = activeBoard?.id;

  // SE-5496: reopen targets — see useWorkingStages for the derivation.
  const workingStages = useWorkingStages(workingPhases);

  // SE-5491 / SE-5508: the mounted renderer + its `view_toggled` emission.
  const { viewMode, onViewModeChange } = usePipelineViewMode(activeBoard?.id);
  // Local UI state
  const [playerTags, setPlayerTags] = useState<Record<string, string[]>>({});
  const [isGloballyDragging, setIsGloballyDragging] = useState(false);
  const [dragSource, setDragSource] = useState<"sidebar" | "kanban" | null>(
    null
  );
  // Static refresh-coordination signal threaded to the columns. The loading
  // retry now re-issues the board queries directly via useKanbanLoadingTimeout,
  // so this no longer needs to be bumped (SE-5237).
  const boardRefreshTrigger = 0;

  // Board sharing state
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);

  // Board permissions from GetKanbanBoardWithPermissions query
  // Note: This may return incorrect values for ROSTER boards due to backend bug (SE-3569)
  const {
    canManageMembers,
    canEdit: canEditFromPermissions,
    canTransferOwnership,
    userRole,
    isOwner: isOwnerFromPermissions,
  } = useBoardPermissions(currentBoardId);

  // SE-8296/SE-8329: the OR lives in utils/canShareBoard, with its reasoning.
  const canOfferShare = canShareBoard(canManageMembers, canTransferOwnership);

  // Get user role from board data (ListMyKanbanBoards query) - SE-3569 workaround
  // This works around backend bug where GetKanbanBoardWithPermissions returns empty users for ROSTER boards
  const activeBoardUserRole = useMemo(
    () => deriveActiveBoardUserRole(activeBoard, currentUser?.id),
    [activeBoard, currentUser?.id]
  );

  // Derive effective permissions using board list data as fallback (SE-3569 workaround)
  // The GetKanbanBoardWithPermissions query returns empty users for ROSTER boards
  const effectiveRole = activeBoardUserRole ?? userRole;
  const isOwner =
    isOwnerFromPermissions || effectiveRole === UserKanbanBoardRole.Owner;
  const canEdit =
    canEditFromPermissions ||
    effectiveRole === UserKanbanBoardRole.Owner ||
    effectiveRole === UserKanbanBoardRole.Editor;

  // Drag overlay state - moved from MainView
  const [active, setActive] = useState<ActiveDragState>(null);

  // SE-5494: one navigator shared by the board's small-screen card tap and the
  // preview drawer's profile/notes actions (see the hook's docblock).
  const navigateToPlayerProfile = usePlayerProfileNavigation();

  const handleOpenPlayerProfile = useCallback(
    (playerId: string) => navigateToPlayerProfile(playerId),
    [navigateToPlayerProfile]
  );

  // Same navigation, aimed at the tab that owns the note list.
  const handleViewAllNotes = useCallback(
    (playerId: string) => navigateToPlayerProfile(playerId, { tab: "notes" }),
    [navigateToPlayerProfile]
  );

  // Use Apollo reactive variable for player sidebar state
  const { selectedPlayerId, setSelectedPlayer } = usePlayerSidebar();

  const sensors = useSensors(
    useSensor(PointerSensor, pointerSensorOptions),
    useSensor(TouchSensor, touchSensorOptions),
    useSensor(KeyboardSensor, keyboardSensorOptions)
  );

  // Only enable drag sensors if user has edit permissions
  const activeSensors = useMemo(
    () => (canEdit ? sensors : []),
    [canEdit, sensors]
  );

  // Board filter state. Filters apply to the left-panel (unplaced) player pool;
  // placed cards on the board are always rendered regardless of the filter.
  const [boardFilters, setBoardFilters] = useState(DEFAULT_FILTER_STATE);

  // SE-6488: board-scoped player search + board-level sort, driven by the
  // header toolbar row. Unlike `boardFilters` (which scopes the left rail's
  // unplaced pool), these apply to the CARDS ON THE BOARD — the AC requires the
  // search to hide non-matching cards across all stages, so the derivation sits
  // here, above both renderers, rather than inside any one column.
  // Resets during render on a board switch, so the new board never paints
  // through the previous board's query/sort — see the hook's module doc.
  const {
    boardSearchQuery,
    boardSort,
    onBoardSearchChange: handleBoardSearchChange,
    onBoardSortChange: handleBoardSortChange,
    onBoardSortDirectionChange: handleBoardSortDirectionChange,
  } = useBoardScopedSearchSort(currentBoardId);

  // SE-6488: what the RENDERERS see. The board search and sort are applied
  // once, here, so the Kanban columns and the Table consume the same filtered
  // dataset ("only cards matching that name, across all stages").
  //
  // Drop handling reads BOTH: a move is persisted against the real board, but
  // any coordinate-derived drop index is measured against these rendered rows
  // and then translated back (see `toFullBoardIndex`). With no active search or
  // sort this returns the input BY REFERENCE, so the default render path keeps
  // its existing referential stability and the memoized columns beneath it do
  // not re-render.
  const visibleColumnsData = useMemo(
    () => applyBoardSearchAndSort(columnsData, boardSearchQuery, boardSort),
    [columnsData, boardSearchQuery, boardSort]
  );

  // Extract available filter options and dynamic ranges from board data.
  // Declared above the table derivation because the dynamic ranges feed its
  // filter-activity detection.
  const { dynamicGprRange, dynamicContractValueRange } =
    useBoardFilterOptions(columnsData);

  // SE-6871: the PERFORMANCE / GOALKEEPER filter scores ride a board-scoped
  // sidecar read (the card query is complexity-capped); the drawer renders
  // those sections only once these have loaded.
  const { metricsById: boardPlayerMetricsById } = useBoardPlayerMetrics(
    activeBoard?.id
  );

  // SE-7144 (Table) / SE-7405 (Kanban): both Watchlist-parity filter panels'
  // committed state, and the narrowed columns each produces, now live in
  // `BoardFilterScope` — mounted below (see the final return) with
  // `key={activeBoard?.id}`, the repo's sanctioned key-reset wrapper
  // (SE-7407). That is what resets `tableAdvancedFilters`/`tableListSelection`
  // and the Kanban panel's `filterState`/`listSelection` on a genuine board
  // switch, instead of letting a selection made on one board silently narrow
  // or empty the next. See `BoardFilterScope.tsx`'s module doc for why an
  // effect was not the fix.

  // Last board we reset filters for, so a transient board re-fetch (id -> null
  // -> same id) does not wipe the user's active filters. Filters are per-board:
  // switching boards resets them so they never leak across boards.
  const lastFilteredBoardIdRef = useRef<string | undefined>(undefined);
  // Last board we logged a view for — same transient-refetch dedupe as above,
  // scoped to analytics so `id -> null -> same id` doesn't double-log.
  const lastLoggedBoardIdRef = useRef<string | undefined>(undefined);

  const [scrollViewLayout, setScrollViewLayout] = useState({
    width: 0,
    x: 0,
  });

  const scrollViewRef = useRef<KanbanScrollViewRef | null>(null);

  // Note: Removed old drag overlay portal system - now using @dnd-kit's DragOverlay exclusively

  const handleColumnTitleChange = useCallback(
    (index: number, newTitle: string) => {
      if (columnsData[index]) {
        updatePhaseName(columnsData[index].id, newTitle);
      }
    },
    [columnsData, updatePhaseName]
  );

  const handleAddColumn = useCallback(() => {
    addPhase("New Column");
  }, [addPhase]);

  // SE-6561: the column header's Delete Stage row no longer deletes on press.
  // It opens the SAME DeleteStageDialog the Stage editor uses, so both entry
  // points confirm through one component. Occupancy is counted over the raw
  // `boardCards` — the array `deletePhase`'s guard filters — so the dialog can
  // never offer a confirm the guard would then refuse.
  const { requestDeleteStage, deleteStageDialogProps } =
    useStageDeleteConfirmation({
      stages: phases,
      cards: boardCards,
      deletePhase,
    });

  // Board management handlers. SE-6270: a chip or drawer pick routes through
  // `useBoardSelection`, which consumes the miss signal this handler used to
  // discard — see that hook's module note for how the drawer can list a board
  // the manager has not got, and what each outcome is allowed to claim.
  const handleBoardSelect = useSwitcherBoardSelection({
    toast,
    switchBoard,
    boardSetPending: isResolvingBoardSet || currentUser === undefined,
    hasUnreachableBoards,
  });

  // SE-5487: the "+ New pipeline" creation flow. On the PIPELINE surface the
  // create button opens the template picker modal (one CreateKanbanBoard
  // mutation; the BE seeds stages; CUSTOM opens the Stage Editor); the legacy
  // WATCHLIST/ROSTER surfaces keep their prior auto-named create behavior.
  const { handleCreateBoard, newPipelineModal } = usePipelineCreationFlow({
    sourceType,
    createBoard,
    boardCount: boards.length,
  });

  const handleMoreBoardOptions = useCallback(() => {
    // More board options are now handled by the BoardOptionsMenu
  }, []);

  const handleDeleteBoard = useCallback(
    async (boardId: string) => {
      try {
        await deleteBoard(boardId);
      } catch (error) {
        console.error("Failed to delete board:", error);
        throw error; // Re-throw to be handled by the UI
      }
    },
    [deleteBoard]
  );

  // TODO: Implement handleDuplicateBoard when duplicateBoard is available in useBoardManager
  // const handleDuplicateBoard = useCallback(
  //   async (boardId: string) => {
  //     try {
  //       await duplicateBoard(boardId);
  //     } catch (error) {
  //       console.error("Failed to duplicate board:", error);
  //       throw error; // Re-throw to be handled by the UI
  //     }
  //   },
  //   [duplicateBoard]
  // );

  const handleUpdateBoardName = useCallback(
    async (newName: string) => {
      if (activeBoard) {
        await updateBoardNameManager(activeBoard.id, newName);
      }
    },
    [activeBoard, updateBoardNameManager]
  );

  /*
   * SE-7470: the commit handler that used to live here is gone with the
   * FilterModal that called it. `boardFilters` now has exactly one writer —
   * the board-switch reset below — so it holds DEFAULT_FILTER_STATE for the
   * whole life of a board.
   *
   * That was ALREADY true in production before this deletion: the commit path
   * ran through the FilterModal, which has not mounted since SE-7405 made
   * `isKanbanFilterMode` unconditional. So `applyBoardFiltersToColumns` below
   * short-circuits on `activeCount === 0` and returns its input unchanged, and
   * `resolveServerGpvRange` resolves null. The state is inert, not merely
   * unused, and collapsing it is left as a separate reviewed change rather
   * than smuggled into this one — see the ticket.
   */

  // Active GPV bounds for the server-side listWatchlistPlayers filter
  // (SE-6496). Null while the cost filter spans the full slider range.
  const serverGpvRange = useMemo(
    () =>
      resolveServerGpvRange(
        boardFilters.contractValueRange,
        dynamicContractValueRange
      ),
    [boardFilters.contractValueRange, dynamicContractValueRange]
  );

  // Handle board refresh (minimal logging)
  const handleBoardRefresh = useCallback(() => {
    // Board refresh is now handled automatically by the useKanbanBoard hook
  }, []);

  // Ultra-smooth optimistic player drop handler
  const handlePlayerDropWrapper = useCallback(
    (columnId: string, playerData: unknown) => {
      // Type guard to ensure playerData has required properties
      const typedPlayerData = playerData as DragOverlayPlayerData;

      // Guard against undefined columnsData
      if (!columnsData) {
        return;
      }

      // Check for same column drop to prevent duplicates
      const sourceColumn = columnsData.find(col =>
        col.players.some(p => p.id === typedPlayerData.id)
      );

      if (sourceColumn?.id === columnId) {
        return; // Same column drop - ignore
      }

      // Check if player already exists in target column
      const targetColumn = columnsData.find(col => col.id === columnId);
      if (targetColumn?.players.some(p => p.id === typedPlayerData.id)) {
        return; // Player already in target - ignore
      }

      // Immediate optimistic update with proper deduplication
      try {
        handlePlayerDrop(columnId, typedPlayerData);
      } catch (error) {
        console.error("Backend drop operation failed:", error);
        // Error handling is managed within handlePlayerDrop
      }
    },
    [columnsData, handlePlayerDrop]
  );

  // Define createPlayerDropHandler early for drag handlers
  const createPlayerDropHandler = useCallback(
    (columnId: string) => (playerData: unknown) =>
      handlePlayerDropWrapper(columnId, playerData),
    [handlePlayerDropWrapper]
  );

  const handleGlobalDragStart = useCallback(
    (_draggedData?: unknown, _event?: unknown) => {
      setIsGloballyDragging(true);
    },
    []
  );

  const handleSidebarDragStart = useCallback(
    (_draggedData?: unknown, _event?: unknown) => {
      setIsGloballyDragging(true);
      setDragSource("sidebar");
    },
    []
  );

  const handleKanbanDragStart = useCallback(
    (_draggedData?: unknown, _event?: unknown) => {
      setIsGloballyDragging(true);
      setDragSource("kanban");
    },
    []
  );

  const handleGlobalDragEnd = useCallback(() => {
    setIsGloballyDragging(false);
    setDragSource(null);
  }, []);

  // Optimized player removal handler - moved here for dependency order
  const handleRemovePlayerFromBoard = useCallback(
    async (playerData: DragOverlayPlayerData) => {
      // SE-5500 made `removePlayerFromBoard` REJECT on failure so the drawer's
      // confirmation dialog can stay open. This drag-to-remove path has no
      // dialog to keep open and its failure is already surfaced by the
      // mutation's own toast, so it absorbs the rejection rather than letting
      // it escape as an unhandled promise rejection.
      try {
        await removePlayerFromBoard(playerData.id);
      } catch {
        // Absorbed on purpose — see above.
      }
    },
    [removePlayerFromBoard]
  );

  // Enhanced drag handlers - moved from MainView
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active: dragActive } = event;

      // Set active immediately - overlay appears in same frame
      setActive(dragActive);

      const draggedData = dragActive.data.current as
        | DragOverlayPlayerData
        | undefined;

      // SE-4685: within-column rank reorder does not engage the cross-column
      // drag chrome (drop zones / other-column pointer locking). The sortable
      // list handles the visual reorder in place.
      if (draggedData?.dragType === "rank-reorder") {
        return;
      }

      // Determine source and call appropriate handler
      if (draggedData?.source === "sidebar") {
        handleSidebarDragStart(draggedData, event);
      } else if (draggedData?.source === "kanban") {
        handleKanbanDragStart(draggedData, event);
      } else {
        handleGlobalDragStart(draggedData, event);
      }
    },
    [handleSidebarDragStart, handleKanbanDragStart, handleGlobalDragStart]
  );

  // Extract column reordering logic to reduce cognitive complexity
  const handleColumnReorder = useCallback(
    (activeColumnId: string, overColumnId: string) => {
      // Guard against undefined columnsData
      if (!columnsData || columnsData.length === 0) {
        return;
      }

      // Find indices
      const oldIndex = columnsData.findIndex(col => col.id === activeColumnId);
      const newIndex = columnsData.findIndex(col => col.id === overColumnId);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Calculate new order
      const reorderedColumns = arrayMove(columnsData, oldIndex, newIndex);

      // SE-6535: both reorder paths (this one and the Stages editor panel) now
      // share one position-assignment scheme, so neither can re-issue a
      // position the board still holds and trip the backend's non-deferred
      // unique (boardId, position) constraint mid-transaction.
      const phasePositions = computePhasePositions(
        reorderedColumns.map(col => col.id),
        { currentPositions: phases.map(phase => phase.position) }
      );

      // Call updatePhasePositions with optimistic update
      if (updatePhasePositions && activeBoard?.id) {
        updatePhasePositions(activeBoard.id, phasePositions);
      }
    },
    [columnsData, phases, updatePhasePositions, activeBoard]
  );

  // SE-7407: `parityColumnsData` (the rendered, kanban-filter-narrowed
  // columns a coordinate-derived drop index must be measured against) now
  // comes from `BoardFilterScope`, a remountable child keyed by
  // `activeBoard?.id` — see that component's module doc. `handleDragEnd`
  // itself lives in THIS component, which never remounts on a board switch,
  // so it cannot close over that value directly (it would either go stale
  // across a re-render of the child, or force `onDragEnd` to be rebuilt every
  // render, which the `react-perf/jsx-no-new-function-as-prop` budget
  // forbids). The render prop writes the latest value into this ref instead
  // — a plain ref write during render, read only later, inside the event
  // handler, when a drop actually happens — so `handleDragEnd` stays a
  // single stable callback for the JSX call site to pass by reference.
  const renderedColumnsRef = useRef<ColumnData[]>(columnsData);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active: dragActive, over } = event;

      if (!over) {
        setActive(null);
        handleGlobalDragEnd();
        return;
      }

      const draggedData = dragActive.data.current as
        | DragOverlayPlayerData
        | undefined;
      const dropTarget = over.data.current as DragOverlayPlayerData | undefined;
      // SE-7405: columns as actually PAINTED (search+sort+kanban-filter) —
      // same reference as visibleColumnsData when no kanban filter is active.
      const rendered = renderedColumnsRef.current;

      // SE-4685: within-column rank reorder — discriminated by drag data.
      // flushSync batches the optimistic write with the active-drag clear so the
      // reorder does not flash back a frame (mirrors the SE-4272 fix).
      // SE-5483: a rank drag ending over ANOTHER column routes to the same
      // card-move path as a body drag, inserting at the drop index.
      // SE-6556: ...but only when the RANK HANDLE started the drag — a
      // card-body drag activates the same sortable and must stay in-stage.
      if (draggedData?.dragType === "rank-reorder") {
        flushSync(() => {
          dispatchRankDrop({
            draggedData,
            dropTarget,
            columnsData,
            draggedRect: draggedRectOf(dragActive),
            activatorEvent: event.activatorEvent,
            updateCardPositions,
            createPlayerDropHandler,
            visibleColumnsData: rendered,
            isRankOrderRendered: boardSort.option === "none",
          });
          setActive(null);
          handleGlobalDragEnd();
        });
        return;
      }

      // Handle column reordering
      const isColumnReorder =
        draggedData?.type === "column" && dropTarget?.type === "column";
      if (isColumnReorder) {
        const activeColumnId = draggedData.columnId;
        const overColumnId = dropTarget.columnId;
        if (activeColumnId && overColumnId && activeColumnId !== overColumnId) {
          handleColumnReorder(activeColumnId, overColumnId);
        }
        setActive(null);
        handleGlobalDragEnd();
        return;
      }

      // Handle column drops (player drops onto columns)
      if (dropTarget?.type === "column") {
        const columnId = dropTarget.columnId;
        const column = columnsData.find(col => col.id === columnId);
        // SE-6488/SE-7405: measure the coordinate-derived slot against the
        // RENDERED column, then translate it back to a full-board index —
        // see `toFullBoardIndex` and `resolveRenderedColumn`.
        const visibleColumn = resolveRenderedColumn(rendered, columnId, column);
        const insertionIndex = toFullBoardIndex(
          column?.players ?? [],
          visibleColumn?.players ?? [],
          calculateInsertionIndex(draggedRectOf(dragActive), visibleColumn)
        );

        const enhancedData: DragOverlayPlayerData = {
          ...draggedData,
          insertionIndex,
        } as DragOverlayPlayerData;

        // Batch cache write + drag clear in one flushSync so React commits
        // both in one render, preventing a one-frame race where activeDragId
        // is null with stale columnsData (SE-4272).
        flushSync(() => {
          if (columnId) {
            createPlayerDropHandler(columnId)(enhancedData);
          }
          setActive(null);
          handleGlobalDragEnd();
        });
        return;
      }

      // Handle removal zone drops
      if (isValidRemovalDrop(dropTarget, draggedData) && draggedData) {
        void handleRemovePlayerFromBoard(draggedData).catch(console.error);
      }

      setActive(null);
      handleGlobalDragEnd();
    },
    [
      columnsData,
      // SE-6488/SE-7405: whether the rendered order is still rank order. The
      // rendered dataset itself is read from `renderedColumnsRef` (see
      // above), not a dependency — it changes far more often than this
      // callback's identity needs to, and closing over it directly would
      // require rebuilding `onDragEnd` every render.
      boardSort.option,
      createPlayerDropHandler,
      handleRemovePlayerFromBoard,
      handleGlobalDragEnd,
      handleColumnReorder,
      updateCardPositions,
    ]
  );

  const handleDragCancel = useCallback(() => {
    setActive(null);
    handleGlobalDragEnd();
  }, [handleGlobalDragEnd]);

  const handleDragOver = useCallback((_event: DragOverEvent) => {
    // Auto-scroll logic can be handled here if needed
    // For now, pass through to existing auto-scroll handler
  }, []);

  const _handleDragEnd = useCallback(
    (_activeId: string, _overId: string | null) => {
      // This might not be needed with the new library approach
    },
    []
  );

  const handleTagAdded = useCallback((playerId: string, tag: string) => {
    setPlayerTags(prevTags => ({
      ...prevTags,
      [playerId]: [...(prevTags[playerId] || []), tag],
    }));
  }, []);

  const getPlayerTags = useCallback(
    (playerId: string) => playerTags[playerId] || [],
    [playerTags]
  );

  const handleAutoScroll = useCallback(
    (dragX: number) => {
      const scrollView = scrollViewRef.current;
      if (!scrollView) {
        return;
      }

      const edgeThreshold = 100;
      const scrollSpeed = 15;
      const { width } = scrollViewLayout;
      const relativeX = dragX;

      if (relativeX > 0 && relativeX < width) {
        // Note: ScrollView.scrollTo doesn't expose current offset in types
        // This is a web-specific implementation that uses the native scrollLeft property
        // For React Native, this would need a different approach using onScroll callback
        const currentScrollX =
          (scrollView as unknown as { scrollLeft?: number }).scrollLeft || 0;

        if (relativeX < edgeThreshold) {
          scrollView.scrollTo({
            x: Math.max(0, currentScrollX - scrollSpeed),
            animated: false,
          });
        } else if (relativeX > width - edgeThreshold) {
          scrollView.scrollTo({
            x: currentScrollX + scrollSpeed,
            animated: false,
          });
        }
      }
    },
    [scrollViewLayout]
  );

  const handleScrollViewLayout = useCallback(
    (event: { nativeEvent: { layout: { width: number; x: number } } }) => {
      const { width, x } = event.nativeEvent.layout;
      setScrollViewLayout({ width, x });
    },
    []
  );

  const createColumnTitleHandler = useCallback(
    (index: number) => (newTitle: string) =>
      handleColumnTitleChange(index, newTitle),
    [handleColumnTitleChange]
  );

  const createColumnDeleteHandler = useCallback(
    (columnId: string) => () => requestDeleteStage(columnId),
    [requestDeleteStage]
  );

  // Prepare board data for TopPanel with user roles (SE-3569 workaround)
  const boardsForTopPanel = useMemo(
    (): BoardInfo[] =>
      boards.map(board => ({
        id: board.id,
        name: board.name,
        isActive: board.id === activeBoard?.id,
        userRole: deriveActiveBoardUserRole(board, currentUser?.id),
      })),
    [boards, activeBoard, currentUser?.id]
  );

  const handlePlayerClick = useCallback(
    (playerId: string) => {
      // Small screens can't fit the preview drawer — go to the full player
      // page instead. Gated on SCREEN SIZE, never device type or platform.
      if (isSmallScreen) {
        handleOpenPlayerProfile(playerId);
        return;
      }
      // Desktop: open the preview drawer over the board.
      setSelectedPlayer(playerId);
    },
    [isSmallScreen, handleOpenPlayerProfile, setSelectedPlayer]
  );

  const handleCloseSidebar = useCallback(() => {
    setSelectedPlayer(null);
  }, [setSelectedPlayer]);

  const handleSharePress = useCallback(() => {
    setIsShareSheetOpen(true);
  }, []);

  const handleShareSheetClose = useCallback(() => {
    setIsShareSheetOpen(false);
  }, []);

  // Generate kanbanCards data for the sidebar's "already on board" lookup.
  // IMPORTANT: derived from the UNFILTERED columns so the sidebar always knows
  // which players are placed — even if a board filter is active. Deriving this
  // from filtered columns is what previously made a filtered-out placed player
  // reappear in the left panel as "unplaced" (and then fail to move).
  const kanbanCards = useMemo(() => {
    if (!columnsData) return [];

    return columnsData.flatMap(column =>
      column.players.map(player => ({
        player: { id: player.id },
        kanbanPhase: { id: column.id, name: column.title },
      }))
    );
  }, [columnsData]);

  // SE-5494: everything the preview drawer needs from board state (stages, the
  // selected card's stage, its tags, its days-in-stage). Lives in a hook rather
  // than inline here because this screen is already at its
  // max-lines-per-function ceiling and the derivation carries no screen concerns.
  const drawerData = usePlayerPreviewDrawerData({
    selectedPlayerId,
    columnsData,
    phases,
  });

  // Show loading state while boards are loading or board is initializing
  // Allow rendering when no active board if boards finished loading (so users can create boards)
  const hasBoardsLoaded = !boardsLoading && boards !== undefined;

  // SE-6491: WHICH board this surface shows — and, when it shows none, WHY
  // not. `utils/boardIdentity` documents the four states the retired sentinel
  // conflated and why only `resolved` may produce an id for the wire. The
  // board-list error is read straight off `boardManagerResult` here; the
  // screen used to discard it and render a failed read as "you have no
  // boards".
  // SE-5630: the skeleton half now also reads WHAT the screen holds, because
  // `isLoading` aggregates five reads — see `shouldShowBoardSkeleton`.
  const boardState = useBoardIdentity(boardManagerResult, kanbanBoardResult);

  // Global timeout safety for Kanban loading — arms on the same gate the UI
  // renders on (so the `activeBoard === null` spinner state is also bounded),
  // and its retry re-issues the underlying queries so a stuck load can recover
  // instead of re-entering the same 45s timeout forever (SE-5237).
  const { showTimeoutError, hasTimedOut, handleRetryLoading } =
    useKanbanLoadingTimeout({
      boardsLoading,
      isLoading,
      isShowingLoading: Boolean(boardState.showSkeleton),
      activeBoardId: activeBoard?.id,
      activeBoardName: activeBoard?.name,
      sourceType,
      refreshBoards,
      refetchBoard: refetchKanbanBoard,
    });

  // SE-4994: a big-board update tapped in Home > Updates routes here as
  // `/player-kanban?boardId=<uuid>`. Activate the corresponding board from
  // that param (once boards have loaded) instead of the default/persisted
  // board.
  //
  // SE-7120: the board set arrives in PHASES, and treating the first phase as
  // final is what made a pipeline deep link land on an unrelated board in
  // silence. Two things can still change it after `hasBoardsLoaded` flips:
  //   1. `sourceType` — `useIsPipelineManager` fails closed while `/auth/me`
  //      is in flight, so this screen resolves Watchlist first and only flips
  //      to Pipeline once the role lands, re-querying an entirely different
  //      board list. A pipeline `boardId` cannot match the watchlist phase.
  //   2. The connection walk (`isResolvingBoardSet`) is still fetching pages.
  // While either holds, a miss is provisional and the param stays pending.
  const deepLinkBoardSetPending =
    isResolvingBoardSet || currentUser === undefined;
  const handleUnresolvedBoardDeepLink =
    useUnresolvedBoardDeepLinkToastHandler(toast);

  useBoardDeepLink({
    boards,
    boardsLoaded: hasBoardsLoaded,
    boardSetPending: deepLinkBoardSetPending,
    switchBoard,
    onUnresolved: handleUnresolvedBoardDeepLink,
  });

  // SE-7121: the same feed clickthrough may also carry `playerId` — open that
  // player's preview drawer once the board's cards have loaded, so the click
  // lands on the exact card the update was about. A player without a card on
  // the landed board degrades to the plain board landing.
  usePlayerDeepLink({
    kanbanCards,
    boardReady: Boolean(columnsData) && hasBoardsLoaded,
    openPlayer: setSelectedPlayer,
  });

  // SE-7121: the same feed clickthrough may also carry `playerId` — open that
  // player's preview drawer once the board's cards have loaded, so the click
  // lands on the exact card the update was about. A player without a card on
  // the landed board degrades to the plain board landing.
  usePlayerDeepLink({
    kanbanCards,
    boardReady: Boolean(columnsData) && hasBoardsLoaded,
    activeBoardId: currentBoardId ?? undefined,
    openPlayer: setSelectedPlayer,
  });

  // SE-6916: the active board previously lived only in an in-memory reactive
  // var, so a reload dropped the user on the first board of the source type.
  // Mirroring the RESOLVED board id to sessionStorage gives the hook above
  // something to restore from. Keying on the resolved board (rather than
  // instrumenting every switchBoard caller) is what makes the reporter's "this
  // should be applied to every update" hold: creation, deletion fallback,
  // source-type flips and chip clicks all land here. The mirror is the only
  // sink — writing the id back to `?boardId=` feeds the reader above and loops
  // the screen to death (round 2; see the hook's remarks).
  useBoardSelectionPersistence(currentBoardId);

  // Reset filters when switching boards. `boardFilters` is a single
  // component-level state and MainContainer stays mounted across board
  // switches, so without this a filter set on one board leaks onto the next.
  // Guard on a genuine change to a new, truthy board id so a transient
  // board re-fetch (id -> null -> same id) never wipes the user's filters.
  useEffect(() => {
    if (!currentBoardId) return;
    if (lastFilteredBoardIdRef.current === currentBoardId) return;
    lastFilteredBoardIdRef.current = currentBoardId;
    setBoardFilters(DEFAULT_FILTER_STATE);
  }, [currentBoardId]);

  // SE-4010: feature-scoped analytics — fires once per activated board
  // (initial load and board switches), carrying the board_id the generic
  // route-level screen_view cannot express. A real A -> B -> A switch still
  // logs each activation because the ref tracks the last non-null id.
  useEffect(() => {
    if (!currentBoardId || lastLoggedBoardIdRef.current === currentBoardId) {
      return;
    }
    lastLoggedBoardIdRef.current = currentBoardId;
    void AnalyticsTracker.scouting.logKanbanBoardViewed({
      board_id: currentBoardId,
    });
  }, [currentBoardId]);

  // SE-5508 telemetry. MUST stay above the early returns below — see the hook.
  usePipelineRouteTelemetry(activeBoard?.id, playerCount, isPipelineSurface);

  // SE-8534: checked BEFORE the skeleton gate below. `boardState.showSkeleton`
  // can still read true on a denied board — its `isLoadingBoardContent`
  // aggregate includes the independent watchlist read, which can still be in
  // flight after the board/cards reads have already settled denied — and the
  // skeleton's own 45s-timeout retry (`handleRetryLoading`) would otherwise
  // refetch the very denied board this state exists to stop retrying.
  if (deniedMessage) {
    return (
      <Box className="flex-1 items-center justify-center p-6">
        <Stack className="max-w-md items-center gap-4">
          <Alert>
            <Text
              variant="unstyled"
              className="text-status-error"
              testID="kanban-access-denied-message"
            >
              {deniedMessage}
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  if (boardState.showSkeleton) {
    // Show timeout error UI if loading has timed out
    return showTimeoutError
      ? renderLoadingTimeoutState(handleRetryLoading)
      : renderBoardSkeleton(hasTimedOut);
  }

  // SE-6491: the board list could not be READ — never rendered as `absent`.
  if (boardState.boardIdentity.kind === "unavailable") {
    return renderBoardsUnavailableState(
      boardState.boardIdentity.reason,
      refreshBoards
    );
  }

  // Show error state if board loading failed OR the cards read failed
  // outright (SE-7624) — never an empty board for either.
  if (error || cardsUiTreatment.kind === "failed") {
    return (
      <Box className="flex-1 items-center justify-center p-6">
        <Stack className="max-w-md items-center gap-4">
          <Alert>
            <Text variant="unstyled" className="text-status-error">
              {error || CARDS_ERROR}
            </Text>
          </Alert>
          <Pressable
            onPress={error ? clearError : refetchKanbanBoard}
            className="rounded-none bg-accent-primary px-4 py-2 hover:bg-accent-primary/90"
          >
            <Text variant="unstyled" className="text-content-on-accent">
              Retry
            </Text>
          </Pressable>
        </Stack>
      </Box>
    );
  }

  // SE-7407: everything downstream of this point that used to read
  // `kanbanFilters`/`tableAdvancedFilters`/etc. directly now reads them off
  // the `filters` argument `BoardFilterScope` hands to this render prop.
  // `key={activeBoard?.id}` is the actual fix — see that component's module
  // doc — remounting it on a genuine board switch discards both panels'
  // committed filter/LISTS state instead of letting it survive onto the next
  // board.
  return (
    <BoardFilterScope
      key={activeBoard?.id}
      visibleColumnsData={visibleColumnsData}
      tableColumnsData={tableColumnsData}
      boardSearchQuery={boardSearchQuery}
      boardSort={boardSort}
      boardCards={boardCards}
      boardPlayerMetricsById={boardPlayerMetricsById}
      boardFilters={boardFilters}
      dynamicGprRange={dynamicGprRange}
      dynamicContractValueRange={dynamicContractValueRange}
    >
      {filters => {
        // SE-7407: a plain ref write during render — see `renderedColumnsRef`'s
        // own doc for why `handleDragEnd` reads it here rather than closing
        // over `filters` (owned by a child this component never remounts
        // with).
        renderedColumnsRef.current = filters.kanbanFilters.parityColumnsData;
        return (
          <>
            {cardsUiTreatment.kind === "stale" &&
              renderCardsStaleBanner(refetchKanbanBoard)}
            <MainView
              columnsData={filters.kanbanFilters.parityColumnsData}
              tableColumnsData={filters.parityTableColumnsData}
              onTableFiltersPress={filters.onTableFiltersOpen}
              tableActiveFilterCount={filters.tableActiveFilterCount}
              onKanbanFiltersPress={filters.kanbanFilters.onFiltersOpen}
              kanbanActiveFilterCount={filters.kanbanFilters.activeFilterCount}
              closedGroups={closedGroups}
              workingStages={workingStages}
              onReopenCard={reopenCard}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              boardName={activeBoard?.name || "Player Pipeline"}
              boardId={boardState.queryBoardId}
              boards={boardsForTopPanel}
              hasUnreachableBoards={hasUnreachableBoards}
              isRosterBoard={isRosterBoard}
              isPipelineSurface={isPipelineSurface}
              // SE-5490: the open board's motion decides which source tabs the
              // add-players panel offers. `null`/undefined = migrated board, which
              // is a meaningful value (all three tabs), not a missing one.
              pipelineTemplate={activeBoard?.pipelineTemplate}
              playerCount={playerCount}
              boardFilters={boardFilters}
              boardSearchQuery={boardSearchQuery}
              onBoardSearchChange={handleBoardSearchChange}
              boardSort={boardSort}
              onBoardSortChange={handleBoardSortChange}
              onBoardSortDirectionChange={handleBoardSortDirectionChange}
              serverGpvRange={serverGpvRange}
              isGloballyDragging={isGloballyDragging}
              dragSource={dragSource}
              scrollViewRef={scrollViewRef}
              kanbanCards={kanbanCards}
              boardRefreshTrigger={boardRefreshTrigger}
              sensors={activeSensors}
              active={active}
              onAddColumn={handleAddColumn}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              onDragOver={handleDragOver}
              onUpdateBoardName={handleUpdateBoardName}
              onBoardSelect={handleBoardSelect}
              onCreateBoard={handleCreateBoard}
              onMoreBoardOptions={handleMoreBoardOptions}
              onDeleteBoard={handleDeleteBoard}
              // onDuplicateBoard={handleDuplicateBoard} // TODO: Implement when duplicateBoard is available
              onGlobalDragStart={handleGlobalDragStart}
              onGlobalDragEnd={handleGlobalDragEnd}
              onSidebarDragStart={handleSidebarDragStart}
              onKanbanDragStart={handleKanbanDragStart}
              onRemovePlayerFromBoard={handleRemovePlayerFromBoard}
              onTagAdded={handleTagAdded}
              getPlayerTags={getPlayerTags}
              onAutoScroll={handleAutoScroll}
              onScrollViewLayout={handleScrollViewLayout}
              createColumnTitleHandler={createColumnTitleHandler}
              createPlayerDropHandler={createPlayerDropHandler}
              createColumnDeleteHandler={createColumnDeleteHandler}
              onPlayerClick={handlePlayerClick}
              onBoardRefresh={handleBoardRefresh}
              canShare={canOfferShare}
              canEdit={canEdit}
              isOwner={isOwner}
              userRole={activeBoardUserRole ?? userRole}
              onSharePress={handleSharePress}
            />
            <BoardShareSheet
              boardId={currentBoardId || ""}
              isOpen={isShareSheetOpen}
              onClose={handleShareSheetClose}
            />
            {/*
             * SE-5495: the reason prompt for moves into a stage whose rule requires
             * one. Rendered at the screen root (not inside a column) so it survives
             * the drag ending and any column re-render, and so the promise the move
             * service is awaiting always has a live UI to settle it.
             */}
            {stageMovePromptComponent}
            {renderPlayerPreviewDrawer({
              selectedPlayerId,
              onClose: handleCloseSidebar,
              drawerData,
              boardId: currentBoardId || undefined,
              canEdit,
              isPipelineBoard: isPipelineSurface,
              onMoveToStage: movePlayerToPhase,
              onOpenProfile: handleOpenPlayerProfile,
              onViewAllNotes: handleViewAllNotes,
              // SE-5500: the drawer removes through the SAME board service the drag
              // path uses — one removal code path, one cache reconciliation. It only
              // differs in that the drawer collected a reason first.
              onRemove: removePlayerFromBoard,
            })}
            {/*
             * SE-5487: the "+ New pipeline" creation modal (template picker + name).
             * Rendered at the screen root so it survives board re-renders and owns its
             * own open/creating state via usePipelineCreationFlow.
             */}
            {newPipelineModal}
            {/*
             * SE-6561: the stage-delete confirmation for the column header path. The
             * SAME molecule the Stage editor mounts — mounted at the screen root so it
             * outlives the overflow menu that requested the delete.
             */}
            <DeleteStageDialog {...deleteStageDialogProps} />

            {/* SE-7144: the TABLE view's Watchlist-parity FILTERS drawer. */}
            <TableFilterDrawer
              isOpen={filters.isTableFilterOpen}
              filterState={filters.tableAdvancedFilters}
              listSelection={filters.tableListSelection}
              listOptions={filters.tableListOptions}
              onCommitLists={filters.onTableListSelectionCommit}
              onClose={filters.onTableFiltersClose}
            />

            {/*
             * SE-7405: the KANBAN view's own Watchlist-parity FILTERS drawer —
             * the SAME `TableFilterDrawer` organism Table uses (it's a thin,
             * board-agnostic shell around `AddPlayersFilterPanel`; nothing about it
             * is actually Table-specific), driven by the Kanban-side state above so
             * committing filters narrows the board's own columns instead.
             */}
            <TableFilterDrawer
              isOpen={filters.kanbanFilters.isFilterOpen}
              filterState={filters.kanbanFilters.filterState}
              listSelection={filters.kanbanFilters.listSelection}
              listOptions={filters.kanbanFilters.listOptions}
              onCommitLists={filters.kanbanFilters.onCommitLists}
              onClose={filters.kanbanFilters.onFiltersClose}
            />
          </>
        );
      }}
    </BoardFilterScope>
  );
};

export default MainContainer;
/* eslint-enable max-lines -- legacy */
